/**
 * Phone API Service for AmalQ
 * Comprehensive API service for phone number management and SMS verification
 * Following enterprise-grade patterns from existing codebase
 */

import { apiClient } from '../../../lib/api';
import { normalizePhoneNumber } from '../validation/phoneValidation';

// Response Types
export interface PhoneApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  message: string;
  timestamp: string;
}

// Phone Status Response
export interface PhoneStatusResponse {
  hasPhone: boolean;
  phoneNumber?: string;
  verified: boolean;
  verifiedAt?: string;
  canRemove: boolean;
  twoFactorEnabled?: boolean;
}

// Add Phone Request/Response
export interface AddPhoneRequest {
  phoneNumber: string;
}

export interface AddPhoneResponse {
  success: boolean;
  message: string;
  expiresIn: number; // seconds
  timestamp: string;
}

// Verify Phone Request/Response
export interface VerifyPhoneRequest {
  phoneNumber: string;
  code: string;
}

export interface VerifyPhoneResponse {
  success: boolean;
  verified: boolean;
  message: string;
  timestamp: string;
}

// Change Phone Request/Response
export interface ChangePhoneRequest {
  newPhoneNumber: string;
}

export interface ChangePhoneResponse {
  success: boolean;
  message: string;
  expiresIn: number; // seconds
  timestamp: string;
}

// Remove Phone Response
export interface RemovePhoneResponse {
  success: boolean;
  message: string;
  timestamp: string;
}

// Password Reset Request/Response (Enhanced)
export interface PasswordResetRequest {
  identifier: string;
  identifierType: 'email' | 'phone';
}

export interface PasswordResetResponse {
  success: boolean;
  message: string;
  data?: {
    resetToken?: string;
    expiresAt: string;
  };
  timestamp: string;
}

// Password Reset Verify Request/Response
export interface PasswordResetVerifyRequest {
  identifier: string;
  identifierType: 'email' | 'phone';
  code: string;
}

export interface PasswordResetVerifyResponse {
  success: boolean;
  message: string;
  data: {
    resetToken: string;
    expiresAt: string;
  };
  timestamp: string;
}

// Password Reset Complete Request/Response
export interface PasswordResetCompleteRequest {
  identifier: string;
  identifierType: 'email' | 'phone';
  code: string;
  newPassword: string;
}

export interface PasswordResetCompleteResponse {
  success: boolean;
  message: string;
  timestamp: string;
}

// Error Types
export interface PhoneApiError {
  success: false;
  message: string;
  timestamp: string;
  code?: string;
}

/**
 * Phone API Service
 * All methods include proper error handling and rate limit detection
 */
export const phoneApi = {
  /**
   * Get phone status
   * GET /api/v1/users/profile/phone/status
   */
  async getPhoneStatus(): Promise<PhoneStatusResponse> {
    try {
      console.log('📱 Phone API: getPhoneStatus called');
      // Backend returns phoneVerified/phoneVerifiedAt, we need to map to verified/verifiedAt
      interface BackendPhoneStatusResponse {
        hasPhone: boolean;
        phoneNumber?: string;
        phoneVerified?: boolean;
        verified?: boolean;
        phoneVerifiedAt?: string;
        verifiedAt?: string;
        canRemove: boolean;
        twoFactorEnabled?: boolean;
      }
      const response = await apiClient.get<BackendPhoneStatusResponse>('/users/profile/phone/status');
      console.log('📱 Phone API: Raw response from apiClient:', response);
      console.log('📱 Phone API: response.data structure:', JSON.stringify(response.data, null, 2));

      if (response.success && response.data) {
        // The response.data might be wrapped in another data object
        // Check if response.data.data exists (double-wrapped)
        const responseData = response.data as BackendPhoneStatusResponse & { data?: BackendPhoneStatusResponse };
        const actualData = responseData.data || responseData;
        console.log('📱 Phone API: Actual data to map:', JSON.stringify(actualData, null, 2));
        
        // Map backend response to our interface
        // Backend returns: phoneVerified, phoneVerifiedAt
        // Our interface expects: verified, verifiedAt
        const mappedData: PhoneStatusResponse = {
          hasPhone: actualData.hasPhone,
          phoneNumber: actualData.phoneNumber,
          verified: actualData.phoneVerified || actualData.verified || false,
          verifiedAt: actualData.phoneVerifiedAt || actualData.verifiedAt,
          canRemove: actualData.canRemove,
          twoFactorEnabled: actualData.twoFactorEnabled,
        };
        
        console.log('📱 Phone API: Mapped phone status:', mappedData);
        return mappedData;
      }

      throw new Error(response.message || 'Failed to fetch phone status');
    } catch (error: unknown) {
      console.error('📱 Phone API Error in getPhoneStatus:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch phone status';
      throw new Error(errorMessage);
    }
  },

  /**
   * Add phone number
   * POST /api/v1/users/profile/phone/add
   */
  async addPhoneNumber(request: AddPhoneRequest): Promise<PhoneApiResponse<AddPhoneResponse>> {
    try {
      // Normalize phone number to E.164 format
      const normalizedPhone = normalizePhoneNumber(request.phoneNumber);
      
      console.log('📱 Phone API: addPhoneNumber called with:', { 
        original: request.phoneNumber, 
        normalized: normalizedPhone 
      });

      const response = await apiClient.post<AddPhoneResponse>('/users/profile/phone/add', {
        phoneNumber: normalizedPhone
      });

      if (response.success && response.data) {
        return {
          success: true,
          data: response.data,
          message: response.message || 'Verification code sent successfully',
          timestamp: response.timestamp || new Date().toISOString(),
        };
      }

      throw new Error(response.message || 'Failed to add phone number');
    } catch (error: unknown) {
      console.error('📱 Phone API Error in addPhoneNumber:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to add phone number';
      throw {
        success: false,
        message: errorMessage,
        timestamp: new Date().toISOString(),
      } as PhoneApiError;
    }
  },

  /**
   * Verify phone number
   * POST /api/v1/users/profile/phone/verify
   */
  async verifyPhoneNumber(request: VerifyPhoneRequest): Promise<PhoneApiResponse<VerifyPhoneResponse>> {
    try {
      // Normalize phone number to E.164 format
      const normalizedPhone = normalizePhoneNumber(request.phoneNumber);
      
      console.log('📱 Phone API: verifyPhoneNumber called with:', { 
        phone: normalizedPhone, 
        codeLength: request.code.length 
      });

      const response = await apiClient.post<VerifyPhoneResponse>('/users/profile/phone/verify', {
        phoneNumber: normalizedPhone,
        code: request.code
      });

      if (response.success && response.data) {
        return {
          success: true,
          data: response.data,
          message: response.message || 'Phone verification successful',
          timestamp: response.timestamp || new Date().toISOString(),
        };
      }

      throw new Error(response.message || 'Failed to verify phone number');
    } catch (error: unknown) {
      console.error('📱 Phone API Error in verifyPhoneNumber:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to verify phone number';
      throw {
        success: false,
        message: errorMessage,
        timestamp: new Date().toISOString(),
      } as PhoneApiError;
    }
  },

  /**
   * Change phone number
   * PUT /api/v1/users/profile/phone/change
   */
  async changePhoneNumber(request: ChangePhoneRequest): Promise<PhoneApiResponse<ChangePhoneResponse>> {
    try {
      // Normalize phone number to E.164 format
      const normalizedPhone = normalizePhoneNumber(request.newPhoneNumber);
      
      console.log('📱 Phone API: changePhoneNumber called with:', { 
        original: request.newPhoneNumber, 
        normalized: normalizedPhone 
      });

      const response = await apiClient.put<ChangePhoneResponse>('/users/profile/phone/change', {
        newPhoneNumber: normalizedPhone
      });

      if (response.success && response.data) {
        return {
          success: true,
          data: response.data,
          message: response.message || 'Verification code sent to new phone',
          timestamp: response.timestamp || new Date().toISOString(),
        };
      }

      throw new Error(response.message || 'Failed to change phone number');
    } catch (error: unknown) {
      console.error('📱 Phone API Error in changePhoneNumber:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to change phone number';
      throw {
        success: false,
        message: errorMessage,
        timestamp: new Date().toISOString(),
      } as PhoneApiError;
    }
  },

  /**
   * Remove phone number
   * DELETE /api/v1/users/profile/phone
   */
  async removePhoneNumber(): Promise<PhoneApiResponse<RemovePhoneResponse>> {
    try {
      console.log('📱 Phone API: removePhoneNumber called');
      const response = await apiClient.delete<RemovePhoneResponse>('/users/profile/phone');

      if (response.success && response.data) {
        return {
          success: true,
          data: response.data,
          message: response.message || 'Phone number removed successfully',
          timestamp: response.timestamp || new Date().toISOString(),
        };
      }

      throw new Error(response.message || 'Failed to remove phone number');
    } catch (error: unknown) {
      console.error('📱 Phone API Error in removePhoneNumber:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to remove phone number';
      throw {
        success: false,
        message: errorMessage,
        timestamp: new Date().toISOString(),
      } as PhoneApiError;
    }
  },

  /**
   * Request password reset (enhanced with phone support)
   * POST /api/v1/auth/forgot-password
   */
  async requestPasswordReset(request: PasswordResetRequest): Promise<PhoneApiResponse<PasswordResetResponse>> {
    try {
      // Normalize phone number if identifierType is phone
      const identifier = request.identifierType === 'phone' 
        ? normalizePhoneNumber(request.identifier)
        : request.identifier;
      
      console.log('📱 Phone API: requestPasswordReset called with:', { 
        identifierType: request.identifierType,
        identifier: identifier 
      });

      const response = await apiClient.post<PasswordResetResponse>('/auth/forgot-password', {
        identifier,
        identifierType: request.identifierType
      });

      if (response.success && response.data) {
        return {
          success: true,
          data: response.data,
          message: response.message || 'Reset code sent successfully',
          timestamp: response.timestamp || new Date().toISOString(),
        };
      }

      throw new Error(response.message || 'Failed to request password reset');
    } catch (error: unknown) {
      console.error('📱 Phone API Error in requestPasswordReset:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to request password reset';
      throw {
        success: false,
        message: errorMessage,
        timestamp: new Date().toISOString(),
      } as PhoneApiError;
    }
  },

  /**
   * Verify password reset code
   * POST /api/v1/auth/verify-reset-code
   */
  async verifyPasswordResetCode(request: PasswordResetVerifyRequest): Promise<PhoneApiResponse<PasswordResetVerifyResponse>> {
    try {
      // Normalize phone number if identifierType is phone
      const identifier = request.identifierType === 'phone' 
        ? normalizePhoneNumber(request.identifier)
        : request.identifier;
      
      console.log('📱 Phone API: verifyPasswordResetCode called with:', { 
        identifierType: request.identifierType,
        identifier: identifier,
        codeLength: request.code.length 
      });

      const response = await apiClient.post<PasswordResetVerifyResponse>('/auth/verify-reset-code', {
        identifier,
        identifierType: request.identifierType,
        code: request.code
      });

      if (response.success && response.data) {
        return {
          success: true,
          data: response.data,
          message: response.message || 'Reset code verified successfully',
          timestamp: response.timestamp || new Date().toISOString(),
        };
      }

      throw new Error(response.message || 'Failed to verify reset code');
    } catch (error: unknown) {
      console.error('📱 Phone API Error in verifyPasswordResetCode:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to verify reset code';
      throw {
        success: false,
        message: errorMessage,
        timestamp: new Date().toISOString(),
      } as PhoneApiError;
    }
  },

  /**
   * Complete password reset
   * POST /api/v1/auth/reset-password
   */
  async completePasswordReset(request: PasswordResetCompleteRequest): Promise<PhoneApiResponse<PasswordResetCompleteResponse>> {
    try {
      // Normalize phone number if identifierType is phone
      const identifier = request.identifierType === 'phone' 
        ? normalizePhoneNumber(request.identifier)
        : request.identifier;
      
      console.log('📱 Phone API: completePasswordReset called with:', { 
        identifierType: request.identifierType,
        identifier: identifier,
        codeLength: request.code.length 
      });

      const response = await apiClient.post<PasswordResetCompleteResponse>('/auth/reset-password', {
        identifier,
        identifierType: request.identifierType,
        code: request.code,
        newPassword: request.newPassword
      });

      if (response.success && response.data) {
        return {
          success: true,
          data: response.data,
          message: response.message || 'Password reset successfully',
          timestamp: response.timestamp || new Date().toISOString(),
        };
      }

      throw new Error(response.message || 'Failed to reset password');
    } catch (error: unknown) {
      console.error('📱 Phone API Error in completePasswordReset:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to reset password';
      throw {
        success: false,
        message: errorMessage,
        timestamp: new Date().toISOString(),
      } as PhoneApiError;
    }
  },
};
